import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { validators, validateForm } from '../../utils/validation';

const emptyTds = { section: '', name: '', rate: 0, ledgerAccount: '' };

export default function TdsMaster() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyTds);
  const [editing, setEditing] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const sections = useMemo(() => state.tdsSections || [], [state.tdsSections]);

  const errors = useMemo(() => {
    const { errors } = validateForm({
      section: [validators.required(form.section, 'Section code')],
      name: [validators.required(form.name, 'Nature of payment')],
      rate: [
        validators.positiveNonZero(form.rate, 'TDS rate'),
        validators.maxValue(form.rate, 100, 'TDS rate'),
      ],
      ledgerAccount: [validators.required(form.ledgerAccount, 'TDS ledger account')],
    });
    return errors;
  }, [form]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (f) => setTouched(p => ({ ...p, [f]: true }));

  const handleSave = () => {
    setSubmitted(true);
    setTouched({ section: true, name: true, rate: true, ledgerAccount: true });
    if (hasErrors) return;
    const payload = { ...form, rate: Number(form.rate) || 0 };
    if (editing) {
      dispatch({ type: 'UPDATE_TDS_SECTION', payload: { ...payload, id: editing } });
      addToast('TDS section updated', 'success');
    } else {
      dispatch({ type: 'ADD_TDS_SECTION', payload });
      addToast('TDS section added', 'success');
    }
    close();
  };

  const close = () => { setShowForm(false); setForm(emptyTds); setEditing(null); setTouched({}); setSubmitted(false); };
  const openNew = () => { setForm(emptyTds); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); };
  const openEdit = (row) => { setForm({ section: row.section, name: row.name, rate: row.rate, ledgerAccount: row.ledgerAccount || '' }); setEditing(row.id); setTouched({}); setSubmitted(false); setShowForm(true); };
  const handleDelete = async (row) => {
    const ok = await confirm(`Delete TDS section ${row.section}?`, { title: 'Delete TDS Section', variant: 'danger', confirmLabel: 'Delete' });
    if (ok) { dispatch({ type: 'DELETE_TDS_SECTION', payload: row.id }); addToast('TDS section deleted', 'success'); }
  };

  const columns = [
    { key: 'section', label: 'Section', sortable: true, render: v => <span className="font-mono font-semibold text-blue-700">{v}</span> },
    { key: 'name', label: 'Nature of Payment', sortable: true },
    { key: 'rate', label: 'TDS Rate', align: 'right', render: v => `${v}%` },
    { key: 'ledgerAccount', label: 'Ledger Account', render: v => v || <span className="text-gray-400">Default (TDS Payable / Receivable)</span> },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">TDS Sections</h1>
        <button onClick={openNew} className="btn btn-primary flex items-center gap-2"><Plus size={16} /> Add TDS Section</button>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={sections}
          searchFields={['section', 'name']}
          exportFilename="tds-sections"
          emptyState={{ title: 'No TDS sections', description: 'Add TDS sections (194C, 194J, etc.) with their deduction rates.', actionLabel: 'Add TDS Section', onAction: openNew }}
          actions={(row) => (
            <>
              <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={close} title={editing ? 'Edit TDS Section' : 'Add TDS Section'}>
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Section Code" required error={errors.section} touched={touched.section || submitted} hint="e.g. 194J">
            <input className="input font-mono" value={form.section} onChange={e => setForm({ ...form, section: e.target.value })} onBlur={() => touch('section')} maxLength={12} placeholder="194J" autoFocus />
          </FormField>
          <FormField label="TDS Rate %" required error={errors.rate} touched={touched.rate || submitted}>
            <input className="input text-right" type="number" min="0" max="100" step="0.01" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} onBlur={() => touch('rate')} />
          </FormField>
          <FormField label="Nature of Payment" required error={errors.name} touched={touched.name || submitted} className="md:col-span-2">
            <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} onBlur={() => touch('name')} maxLength={100} placeholder="e.g. Professional / Technical Fees" />
          </FormField>
          <FormField label="TDS Ledger Account" required error={errors.ledgerAccount} touched={touched.ledgerAccount || submitted} className="md:col-span-2" hint="Account (from Account Master) where this TDS posts.">
            <select className="input" value={form.ledgerAccount} onChange={e => setForm({ ...form, ledgerAccount: e.target.value })} onBlur={() => touch('ledgerAccount')}>
              <option value="">-- Select Account --</option>
              {(state.accounts || []).map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </FormField>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={close} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={submitted && hasErrors}>{editing ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
